import React, { useEffect, useState, useRef } from 'react';
import { SlideOver } from './SlideOver';
/**
 * FullScreenModal — the app's standard full-screen modal pattern.
 *
 * Built on SlideOver, it adds the two things every full-screen modal needs:
 *   • a persistent × (top-right) that closes the whole thing
 *   • automatic content reset on each open (the panel stays mounted for its
 *     slide animation, so without this the inner view would linger between
 *     opens — e.g. reopening Plans and landing back on a sub-view)
 *
 * Drop any self-contained flow inside and you get a slide-up (or slide-right)
 * modal with a working close and a fresh start every time:
 *
 *   <FullScreenModal open={modal === 'plans'} onClose={closeModal}>
 *     <CarePlansModal startAt="picker" ... />
 *   </FullScreenModal>
 *
 * Set `resetOnOpen={false}` for flows that manage their own entry state.
 */
export function FullScreenModal({
  open,
  onClose,
  onBack,
  direction = 'up',
  eyebrow,
  eyebrowClass,
  title,
  padded = false,
  z = 'z-40',
  resetOnOpen = true,
  scroll = true,
  children
}: {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  direction?: 'right' | 'up';
  eyebrow?: string;
  eyebrowClass?: string;
  title?: string;
  padded?: boolean;
  z?: string;
  resetOnOpen?: boolean;
  scroll?: boolean;
  children: React.ReactNode;
}) {
  // bump a session key on every closed→open transition so the children
  // remount fresh (the SlideOver itself stays mounted for the animation)
  const [session, setSession] = useState(0);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) setSession(s => s + 1);
    wasOpen.current = open;
  }, [open]);
  return <SlideOver open={open} onBack={onBack} onClose={onClose} direction={direction} eyebrow={eyebrow} eyebrowClass={eyebrowClass} title={title} padded={padded} z={z} scroll={scroll} data-id="element-111">
      {resetOnOpen ? <div key={session} data-id="element-112">{children}</div> : children}
    </SlideOver>;
}